import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Camera, Upload } from 'lucide-react';
import PageHeading from '../../components/PageHeading';

const TYPES = [
  { id: 'ENGINEER', label: 'Engineer' },
  { id: 'INSTALLER', label: 'Installer' },
  { id: 'DESIGNER', label: 'Designer' },
];

const EXPERT_GROUPS = [
  { id: '', label: '—None' },
  { id: 'FIRE', label: 'Fire' },
  { id: 'MECHANICAL', label: 'Mechanical' },
  { id: 'ELECTRICAL', label: 'Electrical' },
  { id: 'PLUMBING', label: 'Plumbing' },
];

const emptyForm = () => ({
  type: 'ENGINEER',
  expert_group: '',
  name: '',
  email: '',
  phone: '',
  company_name: '',
  ss_codes: '',
  can_issue_ps1: false,
  license_number: '',
});

function ExpertForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [extracting, setExtracting] = useState(false);

  const { data: expert } = useQuery({
    queryKey: ['expert', id],
    queryFn: async () => {
      const res = await api.get(`/experts/${id}`);
      return res.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (expert) {
      const ss = expert.ss_codes;
      const ssStr = Array.isArray(ss) ? ss.join(', ') : ss ? String(ss) : '';
      setForm({
        type: expert.type || 'ENGINEER',
        expert_group: expert.expert_group || '',
        name: expert.name || '',
        email: expert.email || '',
        phone: expert.phone || '',
        company_name: expert.company_name || '',
        ss_codes: ssStr,
        can_issue_ps1: !!expert.can_issue_ps1,
        license_number: expert.license_number || '',
      });
    }
  }, [expert]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const applyExtracted = (ex = {}) => {
    setForm((prev) => ({
      ...prev,
      company_name: ex.company_name || ex.name || prev.company_name,
      name: ex.iqp_name || ex.contact_name || ex.name || prev.name,
      email: ex.email || prev.email,
      phone: ex.phone || prev.phone,
      license_number: ex.license_number || prev.license_number,
      ss_codes: ex.ss_code
        ? String(ex.ss_code)
        : Array.isArray(ex.ss_codes)
          ? ex.ss_codes.join(', ')
          : prev.ss_codes,
    }));
  };

  const setPhoto = async (file) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview('');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));

    const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
    const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
    if (isPdf || isTxt) {
      setExtracting(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/partner-companies/extract-preview', fd, { skipErrorToast: true });
        applyExtracted(res.data?.extracted || {});
        toast.success('Fields filled from document ??check and Save');
      } catch {
        toast.error('Could not read document ??fill fields manually');
      } finally {
        setExtracting(false);
      }
    } else {
      toast('Photo attached ??fill fields while looking at it', { icon: '?��' });
    }
  };

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/experts', data),
    onSuccess: () => {
      toast.success('Expert added');
      queryClient.invalidateQueries(['experts']);
      navigate('/network?tab=engineers');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => api.patch(`/experts/${id}`, data),
    onSuccess: () => {
      toast.success('Expert updated');
      queryClient.invalidateQueries(['experts']);
      queryClient.invalidateQueries(['expert', id]);
      navigate('/network?tab=engineers');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const ssArr = form.ss_codes
      ? form.ss_codes
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const payload = {
      type: form.type,
      expert_group: form.expert_group || null,
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      company_name: form.company_name || null,
      ss_codes: ssArr,
      can_issue_ps1: form.can_issue_ps1,
      license_number: form.license_number || null,
    };
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div>
      <PageHeading
        title={isEdit ? 'Edit Expert' : 'Add Expert'}
        backTo="/network?tab=engineers"
        backLabel="Back to Registered Company"
        infoTitle="Expert"
        infoChildren={
          <p>
            Engineers, installers, or designers used for PS/CoA and compliance workflows. Use Camera / Upload on a Form
            12A (or similar) to fill company, name, contact and SS, then Save.
          </p>
        }
      />
      <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow p-6 border border-gray-200 space-y-6">
        {!isEdit && (
          <div className="rounded-xl border border-dashed border-primary-300 bg-primary-50/40 p-4 space-y-3">
            <p className="text-xs text-gray-600">
              Optional: take a photo of paper Form 12A or upload PDF ??extracted fields fill the form below where
              possible.
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  setPhoto(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf,.txt"
                className="hidden"
                onChange={(e) => {
                  setPhoto(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                disabled={extracting}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium disabled:opacity-50"
              >
                <Camera className="w-4 h-4" />
                Camera (paper Form 12A)
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={extracting}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                {extracting ? 'Reading…' : 'Upload photo / PDF'}
              </button>
              {photoFile && (
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="text-xs text-red-600 underline self-center"
                >
                  Remove file
                </button>
              )}
            </div>
            {photoPreview && String(photoFile?.type || '').startsWith('image/') && (
              <div className="rounded-lg border bg-white overflow-hidden flex items-center justify-center max-h-48">
                <img src={photoPreview} alt="Form photo" className="max-h-48 max-w-full object-contain" />
              </div>
            )}
            {photoFile && !String(photoFile.type || '').startsWith('image/') && (
              <p className="text-xs text-gray-600">Attached: {photoFile.name}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            >
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expert Group</label>
            <select
              value={form.expert_group}
              onChange={(e) => setForm((p) => ({ ...p, expert_group: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {EXPERT_GROUPS.map((g) => (
                <option key={g.id || 'none'} value={g.id}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              type="text"
              value={form.company_name}
              onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SS Codes (comma-separated)</label>
            <input
              type="text"
              value={form.ss_codes}
              onChange={(e) => setForm((p) => ({ ...p, ss_codes: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="SS 3.2, SS 9.0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
            <input
              type="text"
              value={form.license_number}
              onChange={(e) => setForm((p) => ({ ...p, license_number: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.can_issue_ps1}
                onChange={(e) => setForm((p) => ({ ...p, can_issue_ps1: e.target.checked }))}
              />
              <span className="text-sm text-gray-700">Can issue PS1</span>
            </label>
          </div>
        </div>
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending || extracting}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : isEdit ? 'Update' : 'Add'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/network?tab=engineers')}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ExpertForm;
